/**
 * Types for the Plan New Counselling wizard, migrated from the untyped Angular
 * `PlanNewCounsellingComponent`. Shapes are inferred from finalSubmit()'s
 * payload and the `res.result` reads in EditCounselling / GetOnePriority /
 * GetOrderTemplate / GetPriorityMaster / getOrderCopyGridMaster.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

/** Generic dropdown option (Angular {label, value}). */
export interface Option {
  label: string;
  value: string;
}

/** Column definition for the staff/landing tables. */
export interface ColumnDef {
  field: string;
  header: string;
}

/** A row in the staff-type table (Angular staffList entry). */
export interface StaffEntry {
  Designation: string;
  DesignationName: string;
  Subject: string | null;
  SubjectName: string | null;
}

/** A priority row (Angular addedPriorityList entry). The trailing blank row has
 *  empty label/value and acts as the "add" placeholder. */
export interface PriorityEntry {
  label: string;
  value: string;
  IndexId: string;
  IsActive: string | number;
  PriorityNo: string | number;
}

/** A removed-priority record accumulated in Angular's remove[] (5-key shape
 *  the API expects for deactivation). */
export interface PriorityRemoveEntry {
  CnsId: string;
  IndexId: string;
  IsActive: number;
  PriorityId: string;
  PriorityNo: string | number;
}

/** A seniority-order row (Angular addedSeniorityList entry). Trailing blank row
 *  is the "add" placeholder. */
export interface SeniorityEntry {
  label: string;
  value: string;
  OrderKey: string;
}

/** A table-grid row (Angular addedTableGridList entry). Trailing blank row is
 *  the "add" placeholder (orderId === ''). */
export interface TableGridEntry {
  key: string;
  value: string[];
  valueLabel: string | string[];
  orderId: string | number;
}

/** The order-copy template form (Angular OrderTemplate FormGroup). */
export interface OrderTemplate {
  IndexId: string;
  Type: string;
  Header: string;
  Reference: string;
  Reference2: string;
  ContentBefore: string;
  ContentAfter: string;
  CopyTo: string;
  SignAuth: string;
  /** attached at submit time in Angular */
  CnslId?: string;
}

/** Step 1 details form (Angular counsellingFirstForm). Dates are held as
 *  Date | null in the form and serialized to yyyy-MM-dd at submit. */
export interface DetailsForm {
  FromDate: Date | string | null;
  ToDate: Date | string | null;
  Title: string;
  CounsellingType: string;
  Directorate: string;
  Location: string;
  CandidateUpdatedBy: string;
}

/** The full record posted to postPlanCounselling (finalSubmit). */
export interface PlanCounsellingRecord {
  IndexId: string;
  FromDate: string;
  ToDate: string;
  Title: string;
  CounsellingType: string;
  CandidateUpdatedBy: string;
  Location: string;
  Directorate: string;
  Subject: string;
  TeacherType: string;
  DesignationName: string;
  schType: string;
  counselling_minority: string;
  SclCategory: string;
  SclManagement: string;
  SclPanel: string;
  VacUpdatedBy: string;
  vacancy_minority: string;
  VacCategory: string;
  VacManagement: string;
  VacPanel: string;
  Level1Approver: string;
  Level2Approver: string;
  AppSts: string;
  VacanyPublish: string;
  SeniorityPublish: string;
  VacancyChallenge: string;
  SeniorityChallenge: string;
  PreSelectVacancies: string;
  UpdateCandidate: string;
  SeniorityOrder: string;
  SeniorityReset: string;
  order_copy_grid: string;
}

/** A landing-list row (Angular newPlanCounsellingList entry). Kept loose since
 *  the API returns many optional status fields consumed by the settings dialog. */
export interface CounsellingListRow {
  IndexId: string;
  Title: string;
  CounsellingType: string;
  Directorate: string;
  Status: string;
  FromDate: string;
  ToDate: string;
  Designation?: string;
  AppSts?: string;
  VacanyPublish?: string;
  SeniorityPublish?: string;
  VacancyChallenge?: string;
  SeniorityChallenge?: string;
  PreSelectVacancies?: string;
  UpdateCandidate?: string;
  VacUptSts?: string;
  same_district_sts?: string;
  willing_sts?: string;
  SeniorityReset?: string;
  [key: string]: any;
}

/** The 10 settings-dialog toggles (Angular inputSwitch booleans). */
export interface SettingsToggles {
  ApplicationStatus: boolean;
  VacanyPublish: boolean;
  SeniorityPublish: boolean;
  VacancyChallenge: boolean;
  SeniorityChallenge: boolean;
  PreSelectVacancies: boolean;
  UpdateCandidate: boolean;
  VacUptSts: boolean;
  same_district_sts: boolean;
  willing_sts: boolean;
}

// ── Landing-list column set (Angular selectedCounClmn) ──────────────────────

export const LANDING_COLUMNS: ColumnDef[] = [
  { field: 'FromDate', header: 'From - To' },
  { field: 'Title', header: 'Title' },
  { field: 'Directorate', header: 'Directorate' },
  { field: 'CounsellingType', header: 'Counselling Type' },
];

// ── Staff table column set (Angular selectedColumns) ────────────────────────

export const STAFF_COLUMNS: ColumnDef[] = [
  { field: 'DesignationName', header: 'Designation' },
  { field: 'SubjectName', header: 'Subject' },
];
